import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  LineChart, 
  BookOpen, 
  Users,
  Mail,
  ArrowRight
} from 'lucide-react';
import { Section, SectionHeader } from '../../../../components/shared/Section';
import { cn } from '../../../../lib/utils';
import { getThemeClass } from '../../../../lib/theme';

const sections = [
  {
    title: "Professional Journey",
    description: "Track record of technical leadership and domain expertise evolution.",
    icon: LineChart,
    path: "/journey"
  },
  {
    title: "Research & Publications",
    description: "Explore publications, patents, and technical workshops.",
    icon: BookOpen,
    path: "/research"
  },
  {
    title: "Community Impact",
    description: "Dedicated to fostering community growth through volunteer leadership.",
    icon: Users,
    path: "/community"
  },
  {
    title: "Contact",
    description: "Connect for technical collaboration, research opportunities, or professional inquiries.",
    icon: Mail,
    path: "/contact"
  }
];

const QuickNav: React.FC = () => {
  const [searchParams] = useSearchParams();
  const debugMode = searchParams.get('debug') === 'analytics';

  return (
    <Section background="white" spacing="sm" width="default">
      <SectionHeader
        title="Explore Further"
        subtitle="Discover detailed insights into technical innovations, research, and professional journey"
        align="center"
      />

    {debugMode && <button 
      onClick={() => {
        window.gtag?.('event', 'test_button_click', {
          event_category: 'engagement',
          event_label: 'test'
        });
      }}
      className="p-2 bg-blue-500 text-white rounded"
    >
      Test Analytics
    </button>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {sections.map((section, index) => {
          const Icon = section.icon;
          return (
            <Link
              key={index}
              to={section.path}
              className="group cursor-pointer"
            >
              <div className={cn(
                "rounded-lg p-6",
                "transition-all duration-300",
                "shadow-sm hover:shadow-lg",
                getThemeClass('colors.background.surface'),
                getThemeClass('colors.primary.inverse.background'),
                'border',
                getThemeClass('colors.border.light'),
                getThemeClass('colors.primary.inverse.border')
              )}>
                <div className="flex items-start space-x-4">
                  <div className={cn(
                    "p-2 rounded-lg",
                    "transition-colors duration-300",
                    getThemeClass('colors.background.light'),
                    getThemeClass('colors.background.surface', 'inverse')
                  )}>
                    <Icon className={cn(
                      "w-6 h-6",
                      "transition-colors duration-300",
                      getThemeClass('colors.primary.main'),
                      getThemeClass('colors.primary.inverse.icon')
                    )} />
                  </div>
                  <div className="flex-1">
                    <h3 className={cn(
                      "text-lg font-semibold mb-1",
                      "transition-colors duration-300",
                      getThemeClass('colors.text.primary'),
                      getThemeClass('colors.primary.inverse.text')
                    )}>
                      {section.title}
                    </h3>
                    <p className={cn(
                      "transition-colors duration-300",
                      getThemeClass('colors.text.secondary'),
                      getThemeClass('colors.primary.inverse.text')
                    )}>
                      {section.description}
                    </p>
                    <div className={cn(
                      "flex items-center mt-4",
                      "text-sm font-medium",
                      "transition-colors duration-300",
                      getThemeClass('colors.primary.main'),
                      getThemeClass('colors.primary.inverse.icon')
                    )}>
                      Learn more
                      <ArrowRight className={cn(
                        "w-4 h-4 ml-1",
                        "transform transition-transform duration-300",
                        "group-hover:translate-x-1"
                      )} />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </Section>
  );
};

export default QuickNav;
